/**
 * Cliente HTTP.
 *
 * A URL base vem de variável de ambiente (spec §8.2): em desenvolvimento o
 * proxy do Vite manda para a API local; em produção é a mesma origem. Nenhum
 * host fixo no código.
 */
const BASE = import.meta.env["VITE_API_BASE"] ?? "/api";

const CHAVE_SESSAO = "madeira.sessao";

/** Papéis, da maior para a menor autoridade — mesma ordem da API. */
export const PAPEIS = ["DONO", "ESCRITORIO", "PATIO"] as const;
export type Papel = (typeof PAPEIS)[number];

export const NOME_DO_PAPEL: Record<Papel, string> = {
  DONO: "Dono",
  ESCRITORIO: "Escritório",
  PATIO: "Pátio",
};

/**
 * Espelha `podeTudoQue` da API.
 *
 * Aqui serve só para DECIDIR O QUE MOSTRAR. Quem decide o que acontece é a
 * API — esconder um botão não é autorização, e um cliente adulterado tem que
 * levar 403 igual. Ver apps/api/src/modulos/auth/servico.ts.
 */
export function podeTudoQue(papel: Papel, minimo: Papel): boolean {
  return PAPEIS.indexOf(papel) <= PAPEIS.indexOf(minimo);
}

export interface Sessao {
  token: string;
  expiraEm: string;
  usuario: { id: string; nome: string; tenantId: string; papel: Papel };
}

export function lerSessao(): Sessao | null {
  try {
    const cru = localStorage.getItem(CHAVE_SESSAO);
    if (!cru) return null;
    const s = JSON.parse(cru) as Sessao;
    if (new Date(s.expiraEm).getTime() <= Date.now()) {
      localStorage.removeItem(CHAVE_SESSAO);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function gravarSessao(s: Sessao): void {
  try { localStorage.setItem(CHAVE_SESSAO, JSON.stringify(s)); } catch { /* modo privado */ }
}

export function limparSessao(): void {
  try { localStorage.removeItem(CHAVE_SESSAO); } catch { /* ignora */ }
}

export class ErroApi extends Error {
  constructor(readonly status: number, mensagem: string) {
    super(mensagem);
    this.name = "ErroApi";
  }
}

async function requisitar<T>(
  caminho: string,
  opcoes: { metodo?: string; corpo?: unknown; semSessao?: boolean } = {},
): Promise<T> {
  const cabecalhos: Record<string, string> = {};
  if (opcoes.corpo !== undefined) {
    cabecalhos["Content-Type"] = "application/json; charset=utf-8";
  }
  if (!opcoes.semSessao) {
    const s = lerSessao();
    if (s) cabecalhos["Authorization"] = `Bearer ${s.token}`;
  }

  // `body` montado condicionalmente: com exactOptionalPropertyTypes a chave
  // precisa estar AUSENTE, não valer undefined.
  const init: RequestInit = { method: opcoes.metodo ?? "GET", headers: cabecalhos };
  if (opcoes.corpo !== undefined) init.body = JSON.stringify(opcoes.corpo);

  const resp = await fetch(`${BASE}${caminho}`, init);

  // 401 em qualquer rota significa sessão morta: limpa e deixa o app voltar
  // ao login, em vez de deixar telas quebradas com dado velho.
  if (resp.status === 401 && !opcoes.semSessao) {
    limparSessao();
    window.dispatchEvent(new CustomEvent("madeira:sessao-expirada"));
  }

  if (resp.status === 204) return undefined as T;

  const texto = await resp.text();

  // A resposta nem sempre é JSON. Sem API atrás do endereço, o que volta é a
  // página de erro da hospedagem — HTML. Deixar o JSON.parse estourar troca a
  // causa real por "Unexpected token '<'", que não diz nada a quem está na
  // tela e manda procurar defeito no lugar errado.
  let dados: unknown = null;
  if (texto) {
    try {
      dados = JSON.parse(texto);
    } catch {
      throw new ErroApi(
        resp.status,
        resp.ok
          ? "resposta não é JSON — a API não está respondendo neste endereço (ver VITE_API_BASE)"
          : `falha ${resp.status}`,
      );
    }
  }

  if (!resp.ok) {
    const msg =
      dados !== null && typeof dados === "object" && "erro" in dados
        ? String((dados as { erro: unknown }).erro)
        : `falha ${resp.status}`;
    throw new ErroApi(resp.status, msg);
  }
  return dados as T;
}

export const api = {
  get: <T>(c: string) => requisitar<T>(c),
  post: <T>(c: string, corpo?: unknown) => requisitar<T>(c, { metodo: "POST", corpo }),
  put: <T>(c: string, corpo?: unknown) => requisitar<T>(c, { metodo: "PUT", corpo }),
  patch: <T>(c: string, corpo?: unknown) => requisitar<T>(c, { metodo: "PATCH", corpo }),
  del: <T>(c: string) => requisitar<T>(c, { metodo: "DELETE" }),

  entrar: (email: string, senha: string) =>
    requisitar<Sessao>("/auth/login", {
      metodo: "POST",
      corpo: { email, senha },
      semSessao: true,
    }),

  sair: () => requisitar<void>("/auth/logout", { metodo: "POST" }),

  /** Confere quem sou eu AGORA — o papel pode ter mudado desde o login. */
  eu: () => requisitar<{ usuarioId: string; tenantId: string; papel: Papel }>("/auth/eu"),

  trocarSenha: (senhaAtual: string, senhaNova: string) =>
    requisitar<void>("/auth/senha", { metodo: "POST", corpo: { senhaAtual, senhaNova } }),
};

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  papel: Papel;
  ativo: boolean;
  criadoEm: string;
  senhaAlteradaEm: string;
}

/* ------------------------------------------------------------- contratos */

export interface Filial {
  id: string; nome: string; cnpj: string;
  cidade: string; uf: string; matriz: boolean;
  razaoSocial: string; crt: number;
}

export interface Parceiro {
  id: string; nome: string; papel: string; documento: string;
  uf: string | null; cidade: string | null;
  regraCubagem: string; coeficienteEmpilhamento: string | null;
}

export interface Produto {
  id: string; codigo: string; forma: string; classe: string | null;
  ncm: string; ativo: boolean; especieId: string; especie: string; nativa: boolean;
}

export interface BitolaSaldo {
  espMm: number | null; largMm: number | null; compM: string | null;
  patio: string; reservado: string; disponivel: string;
  pecasPatio: number; pecasReservadas: number;
}

export interface OrcamentoGravado {
  id: string; numero: number; serie: number;
  regraCubagem: string; volumeTotal: string; valorTotal: string; itens: number;
}

export interface PedidoGravado {
  id: string; numero: number; orcamentoId: string;
  volumeTotal: string; valorTotal: string; itens: number;
}
